import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../../api/client";
import clinicApi from "../../api/clinicClient";
import PortalLayout from "../layout/PortalLayout";
import SectionTabs from "./SectionTabs";
import { Dialog } from "primereact/dialog";

// ── Shared helpers ──────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function formatAmount(val) {
  if (val == null) return "—";
  return `Rs. ${parseFloat(val).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`;
}

function RelativeBanner({ name }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: "#eff6ff", border: "1px solid #bfdbfe",
      borderRadius: 10, padding: "10px 16px", marginBottom: 16,
      fontSize: 13, color: "#1e40af",
    }}>
      <i className="pi pi-users" style={{ fontSize: 14, flexShrink: 0 }} />
      <span>Viewing records for <b>{name}</b></span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Pharmacy invoices — gf.Invoice
// ══════════════════════════════════════════════════════════════════════════

const PHARMACY_STATUS_CFG = {
  "Open":               { bg: "#eff6ff", color: "#2563eb" },
  "Payment Pending":    { bg: "#fef3c7", color: "#b45309" },
  "Partially Paid":     { bg: "#fef3c7", color: "#92400e" },
  "Complete":           { bg: "#ecfdf5", color: "#059669" },
  "Closed-NonPayment":  { bg: "#fef2f2", color: "#dc2626" },
};

function PharmacyStatusTag({ status }) {
  const s = PHARMACY_STATUS_CFG[status] ?? { bg: "#f3f4f6", color: "#6b7280" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: s.bg, color: s.color,
      borderRadius: 20, padding: "3px 10px",
      fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {status}
    </span>
  );
}

function PharmacyInvoiceDialog({ invoice, onClose, navigate }) {
  if (!invoice) return null;

  const outstanding = parseFloat(invoice.net_price) - parseFloat(invoice.paid_amount);

  const handlePrint = () => {
    const discount = parseFloat(invoice.discount) || 0;
    const tax      = parseFloat(invoice.tax_amount) || 0;
    const subtotal = parseFloat(invoice.total_price) || 0;
    const net      = parseFloat(invoice.net_price) || 0;
    const paid     = parseFloat(invoice.paid_amount) || 0;
    const outstanding = net - paid;

    const discountAmount = invoice.isdiscount_per
      ? subtotal * discount / 100
      : discount;
    const taxAmount = subtotal * tax / 100;

    const fmt = (v) => `Rs. ${parseFloat(v).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`;

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Invoice ${invoice.invoice_number}</title>
  <style>
    * { color: #000 !important; background: #fff !important; }
    body { font-family: Arial, sans-serif; font-size: 13px; margin: 0; padding: 24px; }
    h1 { margin: 0 0 4px; font-size: 20px; }
    .pharmacy-header { border-bottom: 2px solid #000; padding-bottom: 12px; margin-bottom: 16px; }
    .meta { display: flex; justify-content: space-between; margin-bottom: 16px; }
    .meta-right { text-align: right; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    th { border-bottom: 2px solid #000; padding: 7px 6px; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.4px; }
    td { padding: 7px 6px; border-bottom: 1px solid #ddd; font-size: 13px; }
    td.right { text-align: right; }
    td.center { text-align: center; }
    .totals { margin-left: auto; width: 300px; border-top: 2px solid #000; margin-top: 4px; }
    .totals-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #ddd; font-size: 13px; }
    .totals-row:last-child { border-bottom: none; }
    .totals-net { font-weight: 700; font-size: 15px; border-top: 2px solid #000 !important; padding-top: 8px !important; margin-top: 4px; }
    .footer { margin-top: 48px; border-top: 1px solid #999; padding-top: 10px; font-size: 11px; text-align: center; }
    @media print { @page { margin: 20mm; size: A4; } }
  </style>
</head>
<body>
  <div class="pharmacy-header">
    <h1>${invoice.pharmacy?.name || ""}</h1>
    ${invoice.pharmacy?.address ? `<div style="margin-top:4px">${invoice.pharmacy.address}</div>` : ""}
    ${invoice.pharmacy?.phone ? `<div>Tel: ${invoice.pharmacy.phone}</div>` : ""}
  </div>

  <div class="meta">
    <div>
      <div style="font-weight:700;font-size:15px">TAX INVOICE</div>
      <div style="margin-top:4px">Invoice #: <b>${invoice.invoice_number}</b></div>
      <div>Date: ${invoice.date ? new Date(invoice.date).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" }) : "—"}</div>
      <div>Status: ${invoice.status}</div>
    </div>
    <div class="meta-right">
      <div style="font-weight:600">Billed to:</div>
      <div style="margin-top:4px">${invoice.customer_name || "Patient"}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Item</th>
        <th style="text-align:right">Unit Price</th>
        <th style="text-align:right">Qty</th>
        <th style="text-align:right">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${invoice.items.map(item => {
        const unitPrice = item.quantity > 0 ? parseFloat(item.total) / item.quantity : 0;
        return `<tr>
          <td>${item.product}</td>
          <td style="text-align:right">${fmt(unitPrice)}</td>
          <td style="text-align:right">${item.quantity}</td>
          <td style="text-align:right">${fmt(item.total)}</td>
        </tr>`;
      }).join("")}
    </tbody>
  </table>

  <div class="totals">
    <div class="totals-row"><span>Subtotal</span><span>${fmt(subtotal)}</span></div>
    ${tax > 0 ? `<div class="totals-row"><span>Tax (${invoice.tax_amount}%)</span><span>${fmt(taxAmount)}</span></div>` : ""}
    ${discount > 0 ? `<div class="totals-row"><span>Discount${invoice.isdiscount_per ? ` (${invoice.discount}%)` : ""}</span><span>− ${fmt(discountAmount)}</span></div>` : ""}
    <div class="totals-row totals-net"><span>Net Total</span><span>${fmt(net)}</span></div>
    ${invoice.is_paid ? `<div class="totals-row"><span>Paid</span><span>${fmt(paid > 0 ? paid : net)}</span></div>` : ""}
    ${!invoice.is_paid && outstanding > 0.01 ? `<div class="totals-row" style="font-weight:600"><span>Outstanding</span><span>${fmt(outstanding)}</span></div>` : ""}
  </div>

  <div class="footer">
    This is a computer-generated invoice &nbsp;·&nbsp; Generated via FitMe Patient Portal &nbsp;·&nbsp; Coffer Smart
  </div>
</body>
</html>`;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <Dialog
      header={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <i className="pi pi-receipt" style={{ color: "var(--brand-primary)", fontSize: 15 }} />
            Invoice {invoice.invoice_number}
          </div>
          <button onClick={handlePrint} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 500,
            background: "var(--surface-bg)", border: "1px solid var(--border-color)",
            cursor: "pointer", fontFamily: "inherit", color: "var(--text-secondary)",
            marginRight: 8,
          }}>
            <i className="pi pi-print" style={{ fontSize: 11 }} />
            Print / Save PDF
          </button>
        </div>
      }
      visible
      onHide={onClose}
      style={{ width: "min(560px, 95vw)" }}
      breakpoints={{ "768px": "95vw" }}
      draggable={false}
      resizable={false}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

        {/* Meta row */}
        <div style={{
          display: "flex", gap: 16, flexWrap: "wrap",
          padding: "12px 14px", background: "var(--surface-bg)",
          borderRadius: 10, fontSize: 13,
        }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Date</div>
            <div style={{ fontWeight: 600 }}>{formatDate(invoice.date)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Status</div>
            <PharmacyStatusTag status={invoice.status} />
          </div>
          {invoice.duration && (
            <div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Supply</div>
              <div style={{ fontWeight: 600 }}>{invoice.duration} days</div>
            </div>
          )}
        </div>

        {/* Items */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 8 }}>
            Items
          </div>
          <div style={{ border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
            {invoice.items.map((item, idx) => (
              <div key={idx} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 14px",
                borderBottom: idx < invoice.items.length - 1 ? "1px solid var(--border-light)" : "none",
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: "var(--brand-light)", color: "var(--brand-primary)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
                }}>
                  <i className="pi pi-tablet" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {item.product}
                    {item.strength && <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 6 }}>{item.strength}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                    {[item.dosage, item.frequency, item.duration ? `${item.duration} days` : null].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{formatAmount(item.total)}</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>Qty: {item.quantity}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div style={{
          background: "var(--surface-bg)", borderRadius: 10,
          padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--text-secondary)" }}>
            <span>Subtotal</span>
            <span>{formatAmount(invoice.total_price)}</span>
          </div>
          {parseFloat(invoice.tax_amount) > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--text-secondary)" }}>
              <span>Tax ({invoice.tax_amount}%)</span>
              <span>{formatAmount(parseFloat(invoice.total_price) * parseFloat(invoice.tax_amount) / 100)}</span>
            </div>
          )}
          {parseFloat(invoice.discount) > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#059669" }}>
              <span>
                Discount
                {invoice.isdiscount_per
                  ? ` (${invoice.discount}%)`
                  : ""}
              </span>
              <span style={{ fontWeight: 500 }}>
                − {invoice.isdiscount_per
                    ? formatAmount(parseFloat(invoice.total_price) * parseFloat(invoice.discount) / 100)
                    : formatAmount(invoice.discount)}
              </span>
            </div>
          )}
          <div style={{
            display: "flex", justifyContent: "space-between",
            fontSize: 15, fontWeight: 700, color: "var(--text-primary)",
            paddingTop: 8, borderTop: "1px solid var(--border-color)",
          }}>
            <span>Total</span>
            <span>{formatAmount(invoice.net_price)}</span>
          </div>
        </div>

        {/* Payment status banner — below totals box */}
        {invoice.is_paid ? (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#ecfdf5", border: "1px solid #bbf7d0",
            borderRadius: 10, padding: "10px 14px", fontSize: 13,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, color: "#059669", fontWeight: 600 }}>
              <i className="pi pi-check-circle" style={{ fontSize: 14 }} />
              Paid in full
            </div>
            <span style={{ fontWeight: 700, color: "#059669" }}>
              {formatAmount(parseFloat(invoice.paid_amount) > 0 ? invoice.paid_amount : invoice.net_price)}
            </span>
          </div>
        ) : parseFloat(invoice.paid_amount) > 0 ? (
          <div style={{
            background: "#fef3c7", border: "1px solid #fde68a",
            borderRadius: 10, padding: "10px 14px", fontSize: 13,
            display: "flex", flexDirection: "column", gap: 6,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", color: "#059669" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <i className="pi pi-check" style={{ fontSize: 11 }} /> Paid
              </span>
              <span style={{ fontWeight: 600 }}>{formatAmount(invoice.paid_amount)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", color: "#b45309", fontWeight: 700, paddingTop: 6, borderTop: "1px solid #fde68a" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <i className="pi pi-exclamation-circle" style={{ fontSize: 11 }} /> Outstanding
              </span>
              <span>{formatAmount(outstanding)}</span>
            </div>
          </div>
        ) : (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#fef2f2", border: "1px solid #fecaca",
            borderRadius: 10, padding: "10px 14px", fontSize: 13,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, color: "#dc2626", fontWeight: 600 }}>
              <i className="pi pi-clock" style={{ fontSize: 14 }} />
              Unpaid
            </div>
            <span style={{ fontWeight: 700, color: "#dc2626" }}>{formatAmount(invoice.net_price)}</span>
          </div>
        )}

        {/* Origin link */}
        {invoice.origin && (
          <div style={{ paddingTop: 4 }}>
            <button
              onClick={() => {
                onClose();
                if (invoice.origin.type === "refill")
                  navigate(`/refills?filter_id=${invoice.origin.id}`);
                else if (invoice.origin.type === "upload")
                  navigate(`/upload?filter_upload=${invoice.origin.id}`);
                else if (invoice.origin.type === "prescription")
                  navigate(`/prescriptions?tab=uploaded&filter_rx=${invoice.origin.id}`);
              }}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "none", border: "none", cursor: "pointer",
                color: "var(--brand-primary)", fontSize: 12.5, fontWeight: 500,
                fontFamily: "inherit", padding: 0,
              }}
            >
              <i className="pi pi-link" style={{ fontSize: 11 }} />
              View linked {invoice.origin.type === "refill" ? "refill request" : invoice.origin.type === "upload" ? "prescription upload" : "prescription"}
              <i className="pi pi-arrow-right" style={{ fontSize: 10 }} />
            </button>
          </div>
        )}
      </div>

    </Dialog>
  );
}

function PharmacyInvoiceCard({ invoice, onView }) {
  const firstItem = invoice.items?.[0];
  const moreCount = (invoice.items?.length ?? 0) - 1;

  return (
    <div className="ph-card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>

      {/* Row 1: date + status */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>
          <i className="pi pi-calendar" style={{ fontSize: 10, marginRight: 5 }} />
          {formatDate(invoice.date)}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {invoice.is_paid && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              background: "#ecfdf5", color: "#059669",
              borderRadius: 20, padding: "3px 9px", fontSize: 11, fontWeight: 600,
            }}>
              <i className="pi pi-check" style={{ fontSize: 9 }} /> Paid
            </span>
          )}
          <PharmacyStatusTag status={invoice.status} />
        </div>
      </div>

      {/* Row 2: invoice number + first item */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: "var(--brand-light)", color: "var(--brand-primary)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
        }}>
          <i className="pi pi-receipt" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
            {invoice.invoice_number}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {firstItem
              ? `${firstItem.product}${moreCount > 0 ? ` +${moreCount} more` : ""}`
              : "No items"}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>
            {formatAmount(invoice.net_price)}
          </div>
          {invoice.duration && (
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{invoice.duration}d supply</div>
          )}
        </div>
      </div>

      {/* Row 4: view button */}
      <button
        onClick={() => onView(invoice)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
          width: "100%", padding: "9px", borderRadius: 10,
          fontSize: 13, fontWeight: 600, fontFamily: "inherit",
          background: "var(--surface-bg)", color: "var(--text-secondary)",
          border: "1px solid var(--border-color)", cursor: "pointer",
        }}
      >
        <i className="pi pi-eye" style={{ fontSize: 12 }} />
        View details
      </button>
    </div>
  );
}

function SummaryCards({ invoices }) {
  const total      = invoices.length;
  const paid       = invoices.filter(i => i.is_paid).length;
  const outstanding = invoices.filter(i => !i.is_paid && ["Open", "Payment Pending", "Partially Paid"].includes(i.status)).length;
  const totalSpend = invoices.filter(i => i.is_paid).reduce((s, i) => s + parseFloat(i.net_price), 0);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
      {[
        { label: "Total Invoices",  value: total,                        bg: "#f3f4f6", color: "#374151"  },
        { label: "Paid",            value: paid,                         bg: "#ecfdf5", color: "#059669"  },
        { label: "Outstanding",     value: outstanding,                  bg: "#fef3c7", color: "#b45309"  },
        { label: "Total Spend",     value: `Rs. ${totalSpend.toLocaleString("en-LK", { minimumFractionDigits: 2 })}`, bg: "#eff6ff", color: "#2563eb", small: true },
      ].map(s => (
        <div key={s.label} style={{
          background: s.bg, borderRadius: 10, padding: "12px 10px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
        }}>
          <span style={{ fontSize: s.small ? 13 : 22, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</span>
          <span style={{ fontSize: 11, color: s.color, fontWeight: 500, textAlign: "center" }}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function PharmacyInvoicesPanel() {
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();
  const relativeId      = searchParams.get("relative_id");
  const relativeName    = searchParams.get("name");

  const [invoices,      setInvoices]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selected,      setSelected]      = useState(null);
  const [statusFilter,  setStatusFilter]  = useState("all");

  useEffect(() => {
    setLoading(true);
    const headers = {};
    if (relativeId) headers["X-Relative-Id"] = relativeId;
    api.get("invoices/", { headers })
      .then(res => setInvoices(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [relativeId]);

  const filtered = statusFilter === "all"
    ? invoices
    : statusFilter === "paid"
      ? invoices.filter(i => i.is_paid)
      : statusFilter === "outstanding"
        ? invoices.filter(i => !i.is_paid && ["Open", "Payment Pending", "Partially Paid"].includes(i.status))
        : invoices;

  return (
    <>
      <PharmacyInvoiceDialog invoice={selected} onClose={() => setSelected(null)} navigate={navigate} />

      {relativeId && relativeName && <RelativeBanner name={relativeName} />}

      {!loading && invoices.length > 0 && <SummaryCards invoices={invoices} />}

      {/* Filter chips */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { key: "all",         label: "All",         count: invoices.length },
          { key: "paid",        label: "Paid",        count: invoices.filter(i => i.is_paid).length },
          { key: "outstanding", label: "Outstanding", count: invoices.filter(i => !i.is_paid && ["Open","Payment Pending","Partially Paid"].includes(i.status)).length },
        ].map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "6px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 500,
              border: `1.5px solid ${statusFilter === key ? "var(--brand-primary)" : "var(--border-color)"}`,
              background: statusFilter === key ? "var(--brand-primary)" : "transparent",
              color: statusFilter === key ? "white" : "var(--text-secondary)",
              cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
            }}
          >
            {label}
            <span style={{
              background: statusFilter === key ? "rgba(255,255,255,0.25)" : "var(--surface-bg)",
              borderRadius: 10, padding: "0 6px", fontSize: 11,
            }}>{count}</span>
          </button>
        ))}
      </div>

      {/* Cards */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, display: "block", marginBottom: 12, color: "var(--brand-primary)" }} />
          <div style={{ fontSize: 13 }}>Loading invoices…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-receipt" style={{ fontSize: 32, display: "block", marginBottom: 12 }} />
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
            {statusFilter === "all" ? "No pharmacy invoices yet" : `No ${statusFilter} invoices`}
          </div>
          <div style={{ fontSize: 13 }}>Your pharmacy bills will appear here once they are raised.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map(inv => (
            <PharmacyInvoiceCard key={inv.id} invoice={inv} onView={setSelected} />
          ))}
        </div>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Clinic invoices — clinic.PatientInvoice
// ══════════════════════════════════════════════════════════════════════════

const CLINIC_PAYMENT_CFG = {
  unpaid:         { bg: "#fef2f2", color: "#dc2626", label: "Unpaid" },
  partially_paid: { bg: "#fef3c7", color: "#b45309", label: "Partially Paid" },
  paid:           { bg: "#ecfdf5", color: "#059669", label: "Paid" },
  waived:         { bg: "#f3f4f6", color: "#374151", label: "Waived" },
  cancelled:      { bg: "#f3f4f6", color: "#6b7280", label: "Cancelled" },
};

function ClinicStatusTag({ status }) {
  const s = CLINIC_PAYMENT_CFG[status] ?? { bg: "#f3f4f6", color: "#6b7280", label: status };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: s.bg, color: s.color,
      borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
}

function ClinicInvoiceDialog({ invoice, onClose }) {
  if (!invoice) return null;
  return (
    <Dialog header={`Invoice ${invoice.invoice_number}`} visible onHide={onClose} style={{ width: "min(480px, 95vw)" }} draggable={false} resizable={false}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", padding: "12px 14px", background: "var(--surface-bg)", borderRadius: 10, fontSize: 13 }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Clinic</div>
            <div style={{ fontWeight: 600 }}>{invoice.clinic_name || "—"}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Doctor</div>
            <div style={{ fontWeight: 600 }}>{invoice.doctor_name ? `Dr. ${invoice.doctor_name}` : "—"}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Status</div>
            <ClinicStatusTag status={invoice.payment_status} />
          </div>
        </div>

        <div style={{ border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
          {(invoice.items || []).map((item, idx) => (
            <div key={item.id || idx} style={{
              display: "flex", justifyContent: "space-between", padding: "10px 14px",
              borderBottom: idx < invoice.items.length - 1 ? "1px solid var(--border-light)" : "none", fontSize: 13,
            }}>
              <span>{item.description}</span>
              <span style={{ fontWeight: 600 }}>{formatAmount(item.amount)}</span>
            </div>
          ))}
        </div>

        <div style={{ background: "var(--surface-bg)", borderRadius: 10, padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 700 }}>
            <span>Total</span><span>{formatAmount(invoice.total_amount)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--text-secondary)" }}>
            <span>Paid</span><span>{formatAmount(invoice.paid_amount)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 600, color: "#dc2626" }}>
            <span>Balance Due</span><span>{formatAmount(invoice.balance_due)}</span>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function ClinicInvoiceCard({ inv, onView }) {
  return (
    <div className="ph-card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>
          <i className="pi pi-calendar" style={{ fontSize: 10, marginRight: 5 }} /> {formatDate(inv.created_at)}
        </span>
        <ClinicStatusTag status={inv.payment_status} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: "var(--brand-light)", color: "var(--brand-primary)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
        }}>
          <i className="pi pi-receipt" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>{inv.invoice_number}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{inv.clinic_name || "—"}</div>
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>{formatAmount(inv.total_amount)}</div>
      </div>
      <button
        onClick={() => onView(inv)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
          width: "100%", padding: "9px", borderRadius: 10, fontSize: 13, fontWeight: 600, fontFamily: "inherit",
          background: "var(--surface-bg)", color: "var(--text-secondary)", border: "1px solid var(--border-color)", cursor: "pointer",
        }}
      >
        <i className="pi pi-eye" style={{ fontSize: 12 }} /> View details
      </button>
    </div>
  );
}

function ClinicInvoicesPanel() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    clinicApi.get("invoices/")
      .then(res => setInvoices(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const openDetail = (inv) => {
    clinicApi.get(`invoices/${inv.id}/`).then(res => setDetail(res.data)).catch(console.error);
  };

  return (
    <>
      <ClinicInvoiceDialog invoice={detail} onClose={() => setDetail(null)} />

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, display: "block", marginBottom: 12, color: "var(--brand-primary)" }} />
          <div style={{ fontSize: 13 }}>Loading invoices…</div>
        </div>
      ) : invoices.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-receipt" style={{ fontSize: 32, display: "block", marginBottom: 12 }} />
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>No clinic invoices yet</div>
          <div style={{ fontSize: 13 }}>Bills from clinic visits will appear here.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {invoices.map(inv => <ClinicInvoiceCard key={inv.id} inv={inv} onView={openDetail} />)}
        </div>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Merged page
// ══════════════════════════════════════════════════════════════════════════

export default function MyInvoices() {
  const [searchParams, setSearchParams] = useSearchParams();
  const relativeId   = searchParams.get("relative_id");
  const relativeName = searchParams.get("name");
  const requestedTab = searchParams.get("tab");
  const [tab, setTab] = useState(requestedTab === "clinic" ? "clinic" : "pharmacy");

  const setTabAndParam = (key) => {
    setTab(key);
    const p = new URLSearchParams(searchParams);
    p.set("tab", key);
    setSearchParams(p, { replace: true });
  };

  return (
    <PortalLayout>
      <div className="page-header">
        <h1 className="page-title">
          {relativeId && relativeName ? `${relativeName}'s Invoices` : "My Invoices"}
        </h1>
        <p className="page-subtitle">
          {tab === "pharmacy" ? "Your pharmacy billing history" : "Billing from every clinic you've visited"}
        </p>
      </div>

      <SectionTabs
        tabs={[
          { key: "pharmacy", label: "Pharmacy", icon: "pi-shop"     },
          { key: "clinic",   label: "Clinic",    icon: "pi-building" },
        ]}
        active={tab}
        onChange={setTabAndParam}
      />

      {tab === "pharmacy" ? <PharmacyInvoicesPanel /> : <ClinicInvoicesPanel />}
    </PortalLayout>
  );
}
